import React, { useEffect, useState } from "react";
import { Question, Questionnaire, QuestionnaireDefinition } from "../common/models";
import { QuestionnaireTaskView } from "../common/QuestionnaireTaskView";
import Typography from "@mui/material/Typography/Typography";

const isZipCodeValid = (question: Question) => {
  const usZipCodePattern = /^\d{5}$/;
  if (!question.value || question.value === '') return true;
  return typeof question.value === 'string' && usZipCodePattern.test(question.value);
};

const Definition: QuestionnaireDefinition = {
  name: 'Demographics',
  key: 'demographics',
  title: 'About you',
  description: '',
  type: 'questionnaire',
  estimatedCompletionTime: '4:45',
  cohort: 'generic',
  groups: [
    {
      key: 'addressInformation',
      content: <Typography variant="h6" component='div'>Address Information</Typography>,
      questions: [
        {
          key: 'country',
          type: 'multiple-choice',
          question: 'Country',
          options: [
            { key: '1', displayName: 'USA' }
          ],
          value: '1',
          defaultValue: '',
          required: true
        },
        {
          key: 'zipcode',
          type: 'number',
          question: 'Zip Code',
          placeholder: 'Enter ZIP code',
          value: '',
          defaultValue: '',
          required: false,
          validation: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'addressInformation');
            const question = group?.questions.find(x => x.key === 'zipcode');
            return isZipCodeValid(question!);
          },
          invalidText: 'Please enter a valid 5-digit US ZIP code.'
        },
      ]
    },
    {
      key: 'genderAndSex',
      questions: [
        {
          key: 'genderIdentitySelection',
          type: 'multiple-choice',
          question: 'What is your gender identity?',
          options: [
            { key: 'female', displayName: 'Female gender identity' },
            { key: 'male', displayName: 'Male gender identity' },
            { key: 'nonBinary', displayName: 'Non-binary or genderqueer gender identity', },
            { key: 'other', displayName: 'Other', followUpQuestions: [
              {
                key: 'otherGenderIdentitySpecified',
                type: 'text',
                placeholder: 'Please specify',
                question: '',
                value: '',
                defaultValue: '',
                required: true
              }
            ]},
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'specifyGenderIdentity',
          type: 'multiple-choice',
          question: 'Please specify gender identity',
          options: [
            { key: 'cis', displayName: 'Cis: same gender as the sex assigned at birth' },
            { key: 'trans', displayName: 'Trans' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'genderAndSex');
            const question = group?.questions.find(x => x.key === 'genderIdentitySelection');
            return question?.value === 'male' || question?.value === 'female';
          }
        },
        {
          key: 'sexAssignedAtBirth',
          type: 'multiple-choice',
          question: 'What was your sex assigned at birth?',
          options: [
            { key: 'female', displayName: 'Female' },
            { key: 'male', displayName: 'Male' },
            { key: 'intersex', displayName: 'Intersex' },
            { key: 'unknown', displayName: 'Unknown' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'sexualOrientationSelection',
          type: 'multiple-choice',
          question: 'What is your sexual orientation?',
          options: [
            { key: 'bisexual', displayName: 'Bisexual' },
            { key: 'heterosexual', displayName: 'Heterosexual (straight)' },
            { key: 'homosexual', displayName: 'Homosexual (e.g. gay, lesbian)' },
            { key: 'other', displayName: 'Other', followUpQuestions: [
              {
                key: 'otherSexualOrientationSpecified',
                type: 'text',
                placeholder: 'Please specify',
                question: '',
                value: '',
                defaultValue: '',
                required: true
              }
            ]},
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value:  '',
          defaultValue: '',
          required: true
        }
      ]
    },
    {
      key: 'raceAndEthnicity',
      questions: [{
        key: 'raceSelection',
        type: 'multiple-choice-multi-select',
        question: 'Which race category best describes you? Choose all that apply',
        options: [
          { key: '1', displayName: 'American Indian or Alaska Native', followUpQuestions: [
            {
              key: 'nativeAmericanRaceSelection',
              type: 'multiple-choice-multi-select',
              question: '',
              options: [
                { key: '1004_1', displayName: 'AMERICAN INDIAN' },
                { key: '1735_0', displayName: 'ALASKA NATIVE' }
              ],
              value: [],
              defaultValue: [],
              required: true
            }
          ]
          },
          { key: '2', displayName: 'Asian', followUpQuestions: [
            {
              key: 'asianRaceSelection',
              type: 'multiple-choice-multi-select',
              question: '',
              options: [
                { key: '2029_7', displayName: 'ASIAN INDIAN' },
                { key: '2030_5', displayName: 'BANGLADESHI' },
                { key: '2031_3', displayName: 'BHUTANESE' },
                { key: '2032_1', displayName: 'BURMESE' },
                { key: '2033_9', displayName: 'CAMBODIAN' },
                { key: '2034_7', displayName: 'CHINESE' },
                { key: '2035_4', displayName: 'TAIWANESE' },
                { key: '2036_2', displayName: 'FILIPINO' },
                { key: '2037_0', displayName: 'HMONG' },
                { key: '2038_8', displayName: 'INDONESIAN' },
                { key: '2039_6', displayName: 'JAPANESE' },
                { key: '2040_4', displayName: 'KOREAN' },
                { key: '2041_2', displayName: 'LAOTIAN' },
                { key: '2042_0', displayName: 'MALAYSIAN' },
                { key: '2043_8', displayName: 'OKINAWAN' },
                { key: '2044_6', displayName: 'PAKISTANI' },
                { key: '2045_3', displayName: 'SRI LANKAN' },
                { key: '2046_1', displayName: 'THAI' },
                { key: '2047_9', displayName: 'VIETNAMESE' },
                { key: '2048_7', displayName: 'IWO JIMAN' },
                { key: '2049_5', displayName: 'MALDIVIAN' },
                { key: '2050_3', displayName: 'NEPALESE' },
                { key: '2051_1', displayName: 'SINGAPOREAN' },
                { key: '2052_9', displayName: 'MADAGASCAR' }
              ],
              value: [],
              defaultValue: [],
              required: true
            }
          ]},
          { key: '3', displayName: 'Black or African American', followUpQuestions: [
            {
              key: 'blackRaceSelection',
              type: 'multiple-choice-multi-select',
              question: '',
              options: [
                { key: '2056_0', displayName: 'BLACK' },
                { key: '2058_6', displayName: 'AFRICAN AMERICAN' },
                { key: '2060_2', displayName: 'AFRICAN' },
                { key: '2067_7', displayName: 'BAHAMIAN' },
                { key: '2068_5', displayName: 'BARBADIAN' },
                { key: '2069_3', displayName: 'DOMINICAN' },
                { key: '2070_1', displayName: 'DOMINICA ISLANDER' },
                { key: '2071_9', displayName: 'HAITIAN' },
                { key: '2072_7', displayName: 'JAMAICAN' },
                { key: '2073_5', displayName: 'TOBAGONIAN' },
                { key: '2074_3', displayName: 'TRINIDADIAN' },
                { key: '2075_0', displayName: 'WEST INDIAN' }
              ],
              value: [],
              defaultValue: [],
              required: true
            }
          ]},
          { key: '4', displayName: 'Native Hawaiian or other Pacific Islander', followUpQuestions: [
            {
              key: 'pacificIslanderRaceSelection',
              type: 'multiple-choice-multi-select',
              question: '',
              options: [
                { key: '2078_4', displayName: 'POLYNESIAN' },
                { key: '2085_9', displayName: 'MICRONESIAN' },
                { key: '2100_6', displayName: 'MELANESIAN' },
                { key: '2500_7', displayName: 'OTHER PACIFIC ISLANDER' }
              ],
              value: [],
              defaultValue: [],
              required: true
            }
          ]},
          { key: '5', displayName: 'White', followUpQuestions: [
            {
              key: 'whiteRaceSelection',
              type: 'multiple-choice-multi-select',
              question: '',
              options: [
                { key: '2108_9', displayName: 'EUROPEAN' },
                { key: '2118_8', displayName: 'MIDDLE EASTERN OR NORTH AFRICAN' },
                { key: '2129_5', displayName: 'ARAB' }
              ],
              value: [],
              defaultValue: [],
              required: true
            }
          ]},
          { key: '6', displayName: 'Canadian Indigenous or Aboriginal', followUpQuestions: [
            {
              key: 'canadianRaceSelection',
              type: 'multiple-choice-multi-select',
              question: '',
              options: [
                { key: 'ca_first_nation', displayName: 'FIRST NATION' },
                { key: 'ca_inuit', displayName: 'INUIT' },
                { key: 'ca_metis', displayName: 'METIS' }
              ],
              value: [],
              defaultValue: [],
              required: true
            }
          ]},
          { key: '7', displayName: 'Other', followUpQuestions: [
            {
              key: 'otherRaceSpecified',
              type: 'text',
              question: '',
              placeholder: 'Please specify',
              value: '',
              defaultValue: '',
              required: true
            }
          ]},
          { key: '8', displayName: 'Prefer not to answer' }
        ],
        value: [],
        defaultValue: [],
        required: true
      },
      {
        key: 'hispanicLatinoStatus',
        type: 'multiple-choice',
        question: 'What is your ethnic or cultural origin?',
        options: [
          { key: 'yes', displayName: 'Hispanic or Latino' },
          { key: 'no', displayName: 'Not Hispanic or Latino' },
          { key: 'noAnswer', displayName: 'Prefer not to answer' }
        ],
        value: '',
        defaultValue: '',
        required: true
      },
      {
        key: 'hispanicLatinoSelection',
        type: 'multiple-choice-multi-select',
        question: 'Check all that apply',
        options: [
          { key: '1', displayName: 'Spaniard' },
          { key: '3', displayName: 'Central American' },
          { key: '4', displayName: 'South American' },
          { key: '9', displayName: 'Caribbean' }
          ],
        value: [],
        defaultValue: [],
        required: true,
        condition: (questionnaire: Questionnaire) => {
          const group = questionnaire.groups.find(x => x.key === 'raceAndEthnicity');
          const question = group?.questions.find(x => x.key === 'hispanicLatinoStatus');
          return question?.value === 'yes';
        }
      }]
    },
    {
      key: 'education',
      questions: [
        {
          key: 'educationLevelSelection',
          type: 'multiple-choice',
          question: 'What is your highest level of education?',
          options: [
            { key: '1', displayName: 'No formal education' },
            { key: '2', displayName: 'Some elementary school' },
            { key: '3', displayName: 'Some secondary or high school education' },
            { key: '4' , displayName: "High School or secondary school degree complete" },
            { key: '5' , displayName: "Some college education" },
            { key: "6", displayName: "Associate's or technical degree complete" },
            { key: '7', displayName: 'College or baccalaureate degree complete' },
            { key: '8', displayName: 'Some post-baccalaureate education' },
            { key: '9', displayName: 'Graduate or professional degree complete' },
            { key: '10', displayName: 'Doctoral or post graduate education' },
            { key: '11', displayName: 'Other', followUpQuestions: [
              {
                key: 'otherEducationLevelSpecified',
                type: 'text',
                question: '',
                placeholder: 'Please specify',
                value: '',
                defaultValue: '',
                required: true
              }
            ]},
            { key: '12', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        }
      ]
    },
    {
      key: 'employmentStatus',
      questions:[
        {
          key: 'employmentStatusSelection',
          type: 'multiple-choice-multi-select',
          question: 'What is your employment status? Choose all that apply',
          options: [
            { key: '1', displayName: 'Employed, freelance' },
            { key: '2', displayName: 'Employed, full time' },
            { key: '3', displayName: 'Employed, part time' },
            { key: '4', displayName: 'Employed, multiple part time jobs' },
            { key: '5', displayName: 'Student' },
            { key: '6', displayName: 'Self-employed' },
            { key: '10', displayName: 'Stay-at-home parent or guardian' },
            { key: '11', displayName: 'Caregiver' },
            { key: '12', displayName: 'Disabled' },
            { key: '13', displayName: 'Between jobs' },
            { key: '7', displayName: 'Retired' },
            { key: '8', displayName: 'Unemployed' },
            { key: '9', displayName: 'Other', followUpQuestions: [
              {
                key: 'otherEmploymentStatusSpecified',
                type: 'text',
                question: '',
                placeholder: 'Please specify',
                value: '',
                defaultValue: '',
                required: true
              }
            ]}
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'occupation',
          type: 'text',
          question: 'What do you consider to be or have been your primary occupation?',
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'isVeteran',
          type: 'multiple-choice',
          question: 'Are you a veteran?',
          options: [
            { key: '1', displayName: 'Yes' },
            { key: '0', displayName: 'No' },
          ],
          value: '',
          defaultValue: '',
          required: true
        },
      ]
    },
    {
      key: 'disabilityQuestions',
      questions: [
        {
          key: 'hearing',
          type: 'multiple-choice',
          question: 'Are you deaf or do you have serious difficulty hearing?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'vision',
          type: 'multiple-choice',
          question: 'Are you blind or do you have serious difficulty seeing, even when wearing glasses?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'cognition',
          type: 'multiple-choice',
          question: 'Because of a physical, mental, or emotional condition, do you have serious difficulty concentrating, remembering, or making decisions?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'mobility',
          type: 'multiple-choice',
          question: 'Do you have serious difficulty walking or climbing stairs?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'selfCare',
          type: 'multiple-choice',
          question: 'Do you have difficulty dressing or bathing?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'independentLiving',
          type: 'multiple-choice',
          question: `Because of a physical, mental, or emotional condition, do you have difficulty doing errands alone such as visiting a doctor's office or shopping?`,
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'colorblind',
          type: 'multiple-choice',
          question: 'Are you colorblind?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'notCertain', displayName: 'Not certain' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'toneDeaf',
          type: 'multiple-choice',
          question: 'Are you tone deaf?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'notCertain', displayName: 'Not certain' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
      ]
    },
    {
      key: 'householdIncome',
      title: 'Optional',
      questions: [
        {
          key: 'householdIncomeUsa',
          type: 'multiple-choice',
          question: 'What was your total household income last year (USD)? Please include all sources of income, including pensions, dividends, alimony, child support, etc.',
          options: [
            { key: '1', displayName: '< $15,000' },
            { key: '2', displayName: '$15,000 to $29,999' },
            { key: '3', displayName: '$30,000 to $$49,999' },
            { key: '4', displayName: '$50,000 to $99,999' },
            { key: '5', displayName: '$100,000 to $149,999' },
            { key: '6', displayName: '$150,000 to $199,999' },
            { key: '7', displayName: '$200,000 to $249,999' },
            { key: '8', displayName: '>$250,000' },
            { key: '9', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: false,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'addressInformation');
            const question = group?.questions.find(x => x.key === 'country');
            // 1 is USA
            return question?.value === '1';
          }
        },
        {
          key: 'financialAssistanceUsa',
          type: 'multiple-choice',
          question: 'Do you receive a significant portion (>20%) of your income through any of the following federally-funded income assistance programs: SNAP, WIC, or Social Security?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: false,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'addressInformation');
            const question = group?.questions.find(x => x.key === 'country');
            // 1 is USA
            return question?.value === '1';
          }
        },
      ]
    },
    {
      key: 'sectionQuestions',
      title: 'Optional',
      questions: [
        {
          key: 'maritalStatusSelection',
          type: 'multiple-choice-multi-select',
          question: 'What is your marital status? Choose all that apply',
          options: [
            { key: '1', displayName: 'Single' },
            { key: '2', displayName: 'Married' },
            { key: '3', displayName: 'In a relationship' },
            { key: '4', displayName: 'Not married' },
            { key: '5', displayName: 'Separated' },
            { key: '6', displayName: 'Divorced' },
            { key: '7', displayName: 'Widowed' }
          ],
          value: [],
          defaultValue: [],
          required: false
        },
        {
          key: 'housingStatusSelection',
          type: 'multiple-choice',
          question: 'What is your current housing status?',
          options: [
            { key: '1', displayName: 'Assisted living' },
            { key: '3', displayName: 'Own home' },
            { key: '4', displayName: 'Rent home' },
            { key: '5', displayName: 'Skilled nursing facility/nursing home' },
            { key: '6', displayName: 'Unhoused-temporarily' },
            { key: '8', displayName: 'Long-term care facility' },
            { key: '9', displayName: 'Retirement community'},
            { key: '10', displayName: 'Live with family'},
            { key: '7', displayName: 'Unhoused-permanently' }
          ],
          value: '',
          defaultValue: '',
          required: false
        },
        {
          key: 'liveByYourself',
          type: 'multiple-choice',
          question: 'Do you live by yourself?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: false
        },
        {
          key: 'numberOfPeople',
          type: 'integer',
          question: 'How many people live with you?',
          value: '',
          defaultValue: '',
          required: false,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'sectionQuestions');
            const question = group?.questions.find(x => x.key === 'liveByYourself');
            return question?.value === 'no';
          }
        }
      ]
    },
    {
      key: 'whoLivesWithYou',
      title: 'Optional',
      description: 'Who lives with you? Choose all that apply',
      condition: (questionnaire: Questionnaire) => {
        const group = questionnaire.groups.find(x => x.key === 'sectionQuestions');
        const liveByYourselfQuestion = group?.questions.find(x => x.key === 'liveByYourself');
        const householdCountQuestion = group?.questions.find(x => x.key === 'numberOfPeople');
        let householdCountValue = null;
        if (householdCountQuestion?.value !== '' && householdCountQuestion?.value != null) {
          const parsed = Number(householdCountQuestion.value);
          householdCountValue = !isNaN(parsed) ? parsed : null;
        }
        return (liveByYourselfQuestion?.value === 'no' && (householdCountValue !== null && householdCountValue > 0));
      },
      questions: [
        {
          key: 'spousePartnerSignificantOther',
          type: 'multiple-choice',
          question: 'Spouse/partner/significant other',
          options: [
            { key: '1', displayName: 'Yes' },
            { key: '0', displayName: 'No' },
          ],
          value: '',
          defaultValue: '',
          required: false,
        },
        {
          key: 'children',
          type: 'multiple-choice',
          question: 'One or more children',
          options: [
            { key: '1', displayName: 'Yes' },
            { key: '0', displayName: 'No' },
          ],
          value: '',
          defaultValue: '',
          required: false,
        },
        {
          key: 'oneOrMoreParent',
          type: 'multiple-choice',
          question: 'One or more parent',
          options: [
            { key: '1', displayName: 'Yes' },
            { key: '0', displayName: 'No' },
          ],
          value: '',
          defaultValue: '',
          required: false,
        },
        {
          key: 'oneOrMoreGrandParent',
          type: 'multiple-choice',
          question: 'One or more grandparent',
          options: [
            { key: '1', displayName: 'Yes' },
            { key: '0', displayName: 'No' },
          ],
          value: '',
          defaultValue: '',
          required: false
        },
        {
          key: 'roommate',
          type: 'multiple-choice',
          question: 'Roommate(s)',
          options: [
            { key: '1', displayName: 'Yes' },
            { key: '0', displayName: 'No' },
          ],
          value: '',
          defaultValue: '',
          required: false
        },
        {
          key: 'otherLiveWith',
          type: 'multiple-choice',
          question: 'Other',
          options: [
            { key: '1', displayName: 'Yes', followUpQuestions: [
              {
                key: 'othersLiveWithSpecified',
                type: 'text',
                question: 'Please specify',
                value: '',
                defaultValue: '',
                required: true
              }
            ]},
            { key: '0', displayName: 'No' },
          ],
          value: '',
          defaultValue: '',
          required: false
        },
      ]
    },
    {
      key: 'transportation',
      title: 'Optional',
      questions: [
        {
          key: 'reliableTransportation',
          type: 'multiple-choice',
          question: 'Do you have a reliable mode of transportation?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: false
        },
        {
          key: 'transportationSelection',
          type: 'multiple-choice',
          question: 'What is your primary mode of transportation?',
          options: [
            { key: '1', displayName: 'Personal vehicle' },
            { key: '2', displayName: 'Shared vehicle' },
            { key: '3', displayName: 'Public transportation' },
            { key: '4', displayName: 'Ride with someone else' },
            { key: '5', displayName: 'Ride service' },
            { key: '6', displayName: 'Bicycle/Motorbike' },
            { key: '7', displayName: 'Walking' },
            { key: '8', displayName: 'Other', followUpQuestions: [
              {
                key: 'otherTransportationSpecified',
                type: 'text',
                question: 'Please specify',
                value: '',
                defaultValue: '',
                required: true
              }
            ]},
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'transportation');
            const question = group?.questions.find(x => x.key === 'reliableTransportation');
            return question?.value === 'yes';
          }
        },
      ]
    }
  ]
};

export const DemographicsDefinition = Definition;

export const Demographics = ({onCompletion}: {onCompletion: () => void}) => {
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>(() => ({
    id: 'demographics',
    ...Definition,
    startedAt: new Date().toISOString(),
    completedAt: null,
    status: 'in-progress',
  }));

  const genderGroup = questionnaire.groups.find(g => g.key === 'genderAndSex');
  const genderIdentityValue = genderGroup?.questions.find(q => q.key === 'genderIdentitySelection')?.value;

  useEffect(() => {
    if (!genderGroup) return;
    const genderQ = genderGroup.questions.find(q => q.key === 'genderIdentitySelection');
    const specifyQ = genderGroup.questions.find(q => q.key === 'specifyGenderIdentity');
    if (!genderQ || !specifyQ || !specifyQ.options) return;
    const gender = genderQ.value;

    // Precompute new display names for cis and trans
    const cisDisplay = gender === 'female'
      ? 'Cis woman: same gender as the sex assigned at birth'
      : gender === 'male'
        ? 'Cis man: same gender as the sex assigned at birth'
        : 'Cis: same gender as the sex assigned at birth';
    const transDisplay = gender === 'female'
      ? 'Trans woman'
      : gender === 'male'
        ? 'Trans man'
        : 'Trans';

    // Only update if any displayName actually needs to change
    const newOptions = specifyQ.options.map(opt => {
      if (opt.key === 'cis' && opt.displayName !== cisDisplay) {
        return { ...opt, displayName: cisDisplay };
      }
      if (opt.key === 'trans' && opt.displayName !== transDisplay) {
        return { ...opt, displayName: transDisplay };
      }
      return opt;
    });

    // If options are unchanged, skip update
    const optionsChanged = specifyQ.options.some((opt, idx) => opt.displayName !== newOptions[idx].displayName);
    if (!optionsChanged) return;

    setQuestionnaire(q => {
      const newGroups = q.groups.map(g => {
        if (g.key !== 'genderAndSex') return g;
        const newQuestions = g.questions.map(qn => {
          if (qn.key !== 'specifyGenderIdentity') return qn;
          return {
            ...qn,
            options: newOptions
          };
        });
        return {
          ...g,
          questions: newQuestions
        };
      });
      return {
        ...q,
        groups: newGroups
      };
    });
  }, [genderIdentityValue, genderGroup]);

  const handleOnCompletion = () => {
    console.log('Demographics completed');

    onCompletion();
  };

  const handleOnChange = (questionnaire: Questionnaire) => {
    console.log('questionnaire', questionnaire);
    setQuestionnaire(questionnaire);
  }

  console.log('Demographics component rendered', questionnaire);

  return (
    <div>
      <QuestionnaireTaskView questionnaire={questionnaire} onChange={handleOnChange} onCompletion={handleOnCompletion  } />
    </div>
  );
}
